import * as React from 'react';
import styled from 'styled-components';

import { Selector } from '@longline/aqua-ui/inputs/Selector';
import { TSimulationKind } from '../../types/IArea';

interface IProps {
  value?: TSimulationKind;
  /** Fired when control value changes. */
  onChange?: (value: TSimulationKind) => void;
}

/**
 * Three models, with Diagenesis folded into Deposition as a checkbox.
 *
 * Diagenesis is not a fourth model: it runs on the deposition result, so it can
 * only be asked for alongside a deposition run. As a radio button it looked
 * like an alternative to deposition when it is really an addition to it.
 *
 * `simulationKind` is unchanged — 'depositionDiagenix' already meant
 * "deposition, and also diagenesis", so this is a presentation change and no
 * `.fins` file needs migrating.
 */
const ModelControl = (props: IProps) => {
  // Both deposition kinds light the Deposition radio; the checkbox tells them
  // apart.
  const isDeposition = props.value === 'deposition' || props.value === 'depositionDiagenix';

  // `radio` matters. Rendered as checkboxes styled as circles, clicking the
  // selected option deselected it and left the whole group blank: Selector
  // mirrors `checked` into internal state and re-syncs only when the prop
  // *changes*, so a click the parent chose to ignore desynced the two
  // permanently. Real radios sharing a name cannot be unchecked by clicking,
  // so the browser enforces exactly one selection and no change event fires
  // for a click on the current one.
  const selectKind = (value: any) => props.onChange?.(value as TSimulationKind);

  const toggleDiagenesis = (on: any) =>
    props.onChange?.(on ? 'depositionDiagenix' : 'deposition');

  return (
    <Wrapper>
      <Selector
        radio name="simulationKind" value="deposition" dark type='circle'
        label="Deposition"
        checked={isDeposition}
        onChange={selectKind}/>
      {/* Disabled rather than hidden when another model is selected: hiding it
          would shift the two radios below it every time the model changes. */}
      <Indent>
        <Selector
          name="diagenesis" dark
          label="Also run Diagenesis"
          checked={props.value === 'depositionDiagenix'}
          disabled={!isDeposition}
          onChange={toggleDiagenesis}/>
      </Indent>
      <Selector
        radio name="simulationKind" value="dispersion" dark type='circle'
        label="Dispersion"
        checked={props.value === 'dispersion'}
        onChange={selectKind}/>
      <Selector
        radio name="simulationKind" value="pathogens" dark type='circle'
        label="Pathogens"
        checked={props.value === 'pathogens'}
        onChange={selectKind}/>
    </Wrapper>
  );
}

const Indent = styled.div`
  padding-left: 24px;
`

const Wrapper = styled.div`
  padding: 8px 0 8px 24px;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 4px;
  width: 100%;
`

export { ModelControl }
